import React from "react";
import { Box } from "@mui/material";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";

/**
 * Pill-segmented tab picker. The active tab is rendered as a white pill with
 * a small drop shadow; inactive tabs share the container's gray background.
 *
 * Per Figma:
 *   - Energy & Training: HR / Watts / Speed and kcal/min / gr/min (node 14711:148285)
 *   - Body Composition: Lean / Fat / ECW/TBW / ECW / ICW / TBW (node 14725:50370)
 *
 * Wraps a list of string options. The caller owns the active value + handler.
 * `fullWidth` makes each tab share the row equally — used by Body Composition
 * where the picker spans the full panel width.
 */
type Props = {
  options: string[];
  active: string;
  onSelect: (v: string) => void;
  fullWidth?: boolean;
};

const PillTabs: React.FC<Props> = ({
  options,
  active,
  onSelect,
  fullWidth = false,
}) => {
  const theme: Theme = useTheme();
  return (
    <Box sx={styles.container(theme, fullWidth)}>
      {options.map((o) => (
        <Box
          key={o}
          component="button"
          type="button"
          onClick={() => onSelect(o)}
          sx={styles.tab(theme, o === active, fullWidth)}
        >
          {o}
        </Box>
      ))}
    </Box>
  );
};

export default PillTabs;

const styles = {
  container: (theme: Theme, fullWidth: boolean) => ({
    display: "flex",
    alignItems: "center",
    gap: "10px",
    backgroundColor: theme.colorTokens.N[40],
    padding: "4px",
    borderRadius: "8px",
    width: fullWidth ? "100%" : "auto",
  }),
  tab: (theme: Theme, active: boolean, fullWidth: boolean) => ({
    cursor: "pointer",
    border: "none",
    backgroundColor: active
      ? theme.colors_ds.NEUTRAL_BG_MAIN
      : "transparent",
    boxShadow: active ? "0px 2px 4px rgba(0,0,0,0.05)" : "none",
    borderRadius: active ? "10px" : "8px",
    padding: "4px 16px",
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    color: active
      ? theme.colors_ds.NEUTRAL_TEXT_DEFAULT
      : theme.colors_ds.NEUTRAL_TEXT_WEAK,
    flex: fullWidth ? 1 : "0 0 auto",
    whiteSpace: "nowrap" as const,
  }),
};
